import React from 'react';
import { Avatar } from '@fluentui/react-components';
import { PeopleRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { alnumIdToColor, getAccessibleBadgeColors } from '../../Utils';
import { getInitials } from '../Chat.utils';
import styles from './ChatAvatar.module.css';
import { ThemeMode, useProviderTheme } from '../../hooks';

interface ChatAvatarProps {
  variant: 'group' | 'direct';
  ward?: {
    id: string;
    displayName: string;
  };
  badgeCount?: number;
}

/**
 * Avatar representing chat threads or participants.
 */
const ChatAvatarInner: React.FC<ChatAvatarProps> = ({ variant, ward }) => {
  const { t } = useTranslation();
  const theme = useProviderTheme();

  if (variant === 'group') {
    return (
      <Avatar
        icon={<PeopleRegular />}
        aria-label={t('pc:chat:group', { defaultValue: 'Gruppe' })}
        size={48}
      />
    );
  }

  if (!ward) {
    return <Avatar size={48} />;
  }

  const initials = getInitials(ward.displayName);
  const userColor = alnumIdToColor(ward.id);
  const colors =
    theme.mode === ThemeMode.HighContrast ? undefined : getAccessibleBadgeColors(userColor);

  return (
    <div
      className={styles.wardAvatar}
      style={{ backgroundColor: colors?.backgroundColor }}
      role="img"
      aria-label={ward.displayName}
    >
      <span className={styles.initials} style={{ color: colors?.color }} aria-hidden="true">
        {initials}
      </span>
    </div>
  );
};

export const ChatAvatar: React.FC<ChatAvatarProps> = (props) => {
  return (
    <div className={styles.ChatAvatar}>
      {props.badgeCount && <div className={styles.notificationBadge}>{props.badgeCount}</div>}
      <ChatAvatarInner {...props} />
    </div>
  );
};
